/**
 * Bidirectional transformers between new frontend format and legacy API format
 *
 * TODO: Delete this entire file once backend is updated
 */

import type { FilterClause } from "@/lib/routing/types";
import type { Property, DataSource, WebsiteConfig } from "@/lib/types/property";
import type { TrafficMetrics, EventMetrics } from "@/lib/types/metrics";
import { generateSlugId } from "@/lib/routing/slug-id";

/**
 * Transform new frontend formats to legacy API formats
 */
export const transformToLegacy = {
  /**
   * Check if a period is a custom date range
   */
  isCustomPeriod(period: string): boolean {
    return period.includes(":");
  },

  /**
   * Parse custom date range into start/end dates
   * "2025-01-01:2025-01-31" → { start: Date, end: Date }
   */
  parseCustomPeriod(period: string): { start: Date; end: Date } | null {
    if (!period.includes(":")) return null;
    const [startStr, endStr] = period.split(":");
    const start = new Date(startStr);
    const end = new Date(endStr);
    // Set to start/end of day
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  },

  /**
   * Transform period shorthand to legacy format
   * 7d → LAST_7_DAYS
   * 2025-01-01:2025-01-31 → CUSTOM_RANGE:2025-01-01_to_2025-01-31
   * 
   * NOTE: The legacy API does NOT support custom ranges - this returns the format
   * but the API will likely return 500. Use isCustomPeriod() to check first.
   */
  period(period: string): string {
    const periodMap: Record<string, string> = {
      "1d": "PAST_24_H",
      "7d": "LAST_7_DAYS",
      "30d": "LAST_30_DAYS",
      "90d": "LAST_90_DAYS",
      "12m": "LAST_12_MONTHS",
      ytd: "YEAR_TO_DATE",
      mtd: "MONTH_TO_DATE",
    };

    if (periodMap[period]) {
      return periodMap[period];
    }

    // Custom range: "2025-01-01:2025-01-31" → "2025-01-01_2025-01-31"
    if (period.includes(":")) {
      return period.replace(":", "_");
    }

    // Fallback
    return "LAST_7_DAYS";
  },

  /**
   * Transform Filter DSL to legacy filter array
   * country:US,device:mobile → [{ category: 'Location', value: 'US' }, { category: 'Device Type', value: 'Mobile' }]
   */
  filters(filters: FilterClause[]): Array<{ category: string; value: string }> {
    const legacyFilters: Array<{ category: string; value: string }> = [];

    for (const filter of filters) {
      // Only process equality filters for now (legacy API doesn't support comparisons)
      if (filter.operator !== ":") continue;

      const legacyFilter = this.filterClause(filter);
      if (legacyFilter) {
        legacyFilters.push(legacyFilter);
      }
    }

    return legacyFilters;
  },

  /**
   * Transform single filter clause
   */
  filterClause(
    filter: FilterClause
  ): { category: string; value: string } | null {
    // Map new field names to legacy category names
    const fieldMap: Record<string, string> = {
      country: "Location",
      region: "Region",
      device: "Device Type",
      browser: "Browser",
      os: "OS",
      referrer: "Referrer",
      utm_source: "UTM Source",
      utm_campaign: "UTM Campaign",
      page: "Page",
    };

    const category = fieldMap[filter.field];
    if (!category) return null;

    // Map values if needed (e.g., mobile → Mobile)
    let value = filter.value;
    if (filter.field === "device") {
      const deviceMap: Record<string, string> = {
        desktop: "Desktop",
        mobile: "Mobile",
      };
      value = deviceMap[value.toLowerCase()] || value;
    }

    return { category, value };
  },
};

/**
 * Transform legacy API responses to new frontend formats
 */
export const transformFromLegacy = {
  /**
   * Transform legacy filter array to Filter DSL
   * [{ category: 'Location', value: 'US' }, { category: 'Device Type', value: 'Mobile' }] → FilterClause[]
   */
  filters(legacyFilters: Array<{ category: string; value: string }>): FilterClause[] {
    const filters: FilterClause[] = [];

    // Reverse category mapping
    const categoryMap: Record<string, string> = {
      "Location": "country",
      "Region": "region",
      "Device Type": "device",
      "Browser": "browser",
      "OS": "os",
      "Referrer": "referrer",
      "UTM Source": "utm_source",
      "UTM Campaign": "utm_campaign",
      "Page": "page",
    };

    for (const legacyFilter of legacyFilters) {
      const field = categoryMap[legacyFilter.category];
      if (!field) continue;

      // Map values if needed (e.g., Mobile → mobile)
      let value = legacyFilter.value;
      if (legacyFilter.category === "Device Type") {
        const deviceMap: Record<string, string> = {
          "Desktop": "desktop",
          "Mobile": "mobile",
        };
        value = deviceMap[value] || value.toLowerCase();
      }

      filters.push({
        field,
        operator: ":",
        value,
      });
    }

    return filters;
  },

  /**
   * Transform legacy property to new Property model
   * Old: { propertyId, name, domain }
   * New: { propertyId, name, slug, dataSources: [{ type: 'website', config: { domain } }] }
   */
  property(legacyProperty: any): Property {
    const slug = generateSlugId(
      legacyProperty.name || legacyProperty.domain,
      legacyProperty.propertyId
    );

    // Legacy API has 1:1 property-domain mapping
    // Transform to property with single website datasource
    const websiteDataSource: DataSource = {
      dataSourceId: `ds-${legacyProperty.propertyId}-web`, // Generate synthetic ID
      propertyId: legacyProperty.propertyId,
      type: "website",
      name: legacyProperty.name || legacyProperty.domain,
      config: {
        domain: legacyProperty.domain,
        trackingId: legacyProperty.trackingId || legacyProperty.propertyId, // Reuse propertyId as trackingId
      } as WebsiteConfig,
      isActive: true,
      createdAt: legacyProperty.createdAt || new Date().toISOString(),
    };

    return {
      propertyId: legacyProperty.propertyId,
      name: legacyProperty.name || legacyProperty.domain,
      slug,
      dataSources: [websiteDataSource],
      createdAt: legacyProperty.createdAt || new Date().toISOString(),
      updatedAt: legacyProperty.updatedAt || new Date().toISOString(),
    };
  },

  /**
   * Transform legacy traffic metrics to new format
   */
  trafficMetrics(legacyResponse: any): TrafficMetrics {
    // Map legacy response shape to new TrafficMetrics type
    return {
      propertyId: legacyResponse.propertyId || "",
      period: legacyResponse.period || "",
      totalVisitors:
        legacyResponse.visitors || legacyResponse.totalVisitors || 0,
      totalPageViews:
        legacyResponse.pageViews || legacyResponse.totalPageViews || 0,
      bounceRate: legacyResponse.bounceRate || 0,
      avgSessionDuration:
        legacyResponse.avgDuration || legacyResponse.avgSessionDuration || 0,
      timeSeries: legacyResponse.timeSeries || [],
      topPages: legacyResponse.topPages || [],
      topSources:
        legacyResponse.topSources || legacyResponse.topReferrers || [],
      deviceBreakdown: legacyResponse.deviceBreakdown || {},
      locationBreakdown: legacyResponse.locationBreakdown || {},
    };
  },

  /**
   * Transform legacy event metrics
   */
  eventMetrics(legacyResponse: any): EventMetrics {
    return {
      propertyId: legacyResponse.propertyId || "",
      period: legacyResponse.period || "",
      events: legacyResponse.events || [],
      totalEvents: legacyResponse.totalEvents || 0,
    };
  },
};
